import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Loader2, Circle, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

/**
 * Phase 101fh (Feb 2026) — Vitrine machines interne (dashboard mural).
 *
 * Écran dédié aux employés atelier : affichage slot par slot de chaque
 * distributeur automatique, style site-builder-4764 (fond sombre, dorés,
 * épuisés barrés). Zéro clic pour voir ce qui manque.
 *
 * Un pas vers la vision zéro-MoMa : source unique AgroManage, live-refresh
 * toutes les 60s, aucune dépendance externe.
 */

const REFRESH_INTERVAL_MS = 60_000;
const DLC_SHORT_DAYS = 3;

function daysUntil(dlcIso) {
  if (!dlcIso) return null;
  try {
    const d = new Date(dlcIso);
    const now = new Date();
    return Math.ceil((d.getTime() - now.getTime()) / (24 * 3600 * 1000));
  } catch {
    return null;
  }
}

function MachineCard({ machine }) {
  const idx = String(machine.index).padStart(2, "0");
  const emptyRatio = machine.total_slots
    ? machine.empty_slots / machine.total_slots
    : 0;
  const statusColor = emptyRatio > 0.5
    ? "#E27D5A"
    : emptyRatio > 0.2
    ? "#C1A063"
    : "#88A066";
  const statusLabel = emptyRatio > 0.5
    ? "À REMPLIR"
    : emptyRatio > 0.2
    ? "PARTIEL"
    : "OK";

  return (
    <div
      className="border border-white/10 p-6 transition-colors duration-300 hover:border-[#C1A063]/60 flex flex-col"
      data-testid={`vitrine-card-${machine.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <span className="font-mono text-xs text-white/40">{idx}</span>
        <div className="flex items-center gap-2">
          {machine.is_corporate && (
            <span
              className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border border-[#6B4E71] text-[#C9B4D0] bg-[#6B4E71]/20"
              title="Distributeur Corporate — installation privée en entreprise cliente (prix subventionnés). Non visible sur le site public."
              data-testid={`vitrine-badge-corp-${machine.id}`}
            >
              Corporate
            </span>
          )}
          <span
            className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em]"
            style={{ color: statusColor }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: statusColor }}
            />
            {statusLabel}
          </span>
        </div>
      </div>
      <h3 className="font-serif text-2xl mb-1 text-[#F2ECE4]" data-testid={`vitrine-card-name-${machine.id}`}>
        {machine.name}
      </h3>
      {machine.city && (
        <p className="text-sm text-[#C1A063]/80">{machine.city}</p>
      )}
      {machine.address && (
        <p className="font-mono text-[11px] text-white/40 mb-2">{machine.address}</p>
      )}
      <div className="flex items-center gap-3 mt-2 mb-4">
        <span className="font-mono text-[10px] text-white/50">
          {machine.total_slots - machine.empty_slots}/{machine.total_slots} en stock
        </span>
        {machine.empty_slots > 0 && (
          <span className="font-mono text-[10px] text-[#E27D5A]">
            · {machine.empty_slots} épuisé{machine.empty_slots > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div
        className="pt-3 border-t border-white/10 space-y-2 max-h-72 overflow-y-auto pr-1"
        data-testid={`vitrine-slots-${machine.id}`}
      >
        {(machine.items || []).length === 0 && (
          <p className="text-xs text-white/40 italic py-2">Aucun slot mappé</p>
        )}
        {(machine.items || []).map((it) => {
          const empty = it.quantity_available <= 0;
          const shortDlc = daysUntil(it.dlc_shortest);
          const isDlcAlert = shortDlc !== null && shortDlc <= DLC_SHORT_DAYS && !empty;
          return (
            <div
              key={`${machine.id}-${it.slot}-${it.product_id}`}
              className="flex items-center gap-3 text-xs"
              data-testid={`vitrine-slot-${machine.id}-${it.slot}`}
            >
              <span className="font-mono text-white/40 w-6 shrink-0">{it.slot}</span>
              <span
                className={`flex-1 truncate ${
                  empty
                    ? "text-white/40 line-through"
                    : isDlcAlert
                    ? "text-[#E27D5A]"
                    : "text-[#F2ECE4]"
                }`}
                title={it.product_name}
              >
                {it.product_name}
              </span>
              <span className="font-mono text-[#C1A063] shrink-0">
                {it.price_public.toFixed(2)}
              </span>
              <span
                className={`font-mono text-[9px] uppercase tracking-[0.1em] shrink-0 w-16 text-right ${
                  empty ? "text-[#E27D5A]" : isDlcAlert ? "text-[#C1A063]" : "text-white/50"
                }`}
              >
                {empty
                  ? "Épuisé"
                  : isDlcAlert
                  ? `DLC ${shortDlc}j`
                  : `× ${it.quantity_available}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VitrineMachines() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/vitrine/machines");
      setMachines(data?.machines || []);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const summary = useMemo(() => {
    const total = machines.reduce((acc, m) => acc + m.total_slots, 0);
    const empty = machines.reduce((acc, m) => acc + m.empty_slots, 0);
    return { total, empty, filled: total - empty };
  }, [machines]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2ECE4]" data-testid="vitrine-machines-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <Link
              to="/etat-distributeurs"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-[#C1A063] mb-2"
              data-testid="vitrine-back-link"
            >
              <ArrowLeft className="w-3 h-3" /> Retour · État distributeurs
            </Link>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C1A063]">
              Vitrine machines · temps réel
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-1">
              Maison Thaï, <span className="italic text-[#C1A063]">à portée de quartier.</span>
            </h1>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                {machines.length} machine{machines.length > 1 ? "s" : ""} · {summary.filled}/{summary.total} slots
                {summary.empty > 0 && (
                  <span className="text-[#E27D5A] ml-1">· {summary.empty} épuisé{summary.empty > 1 ? "s" : ""}</span>
                )}
              </div>
              {lastRefreshed && (
                <div className="font-mono text-[10px] text-white/30 mt-1">
                  Rafraîchi à {lastRefreshed.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </div>
              )}
            </div>
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className="border border-white/20 hover:border-[#C1A063] px-3 py-2 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors"
              data-testid="vitrine-toggle-auto"
              title={autoRefresh ? "Auto-refresh actif" : "Auto-refresh désactivé"}
            >
              <Circle
                className={`w-2 h-2 ${autoRefresh ? "fill-[#88A066] text-[#88A066]" : "fill-none text-white/40"}`}
              />
              {autoRefresh ? "Live" : "Manuel"}
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="border border-[#C1A063]/50 text-[#C1A063] hover:bg-[#C1A063]/10 px-3 py-2 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 disabled:opacity-40"
              data-testid="vitrine-refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Rafraîchir
            </button>
          </div>
        </div>

        {error && (
          <div className="border border-[#E27D5A]/40 bg-[#E27D5A]/10 text-[#E27D5A] px-4 py-3 mb-6 text-sm" data-testid="vitrine-error">
            {error}
          </div>
        )}

        {loading && machines.length === 0 && (
          <div className="text-center py-16 text-white/40 text-sm">
            <Loader2 className="w-6 h-6 animate-spin inline-block mb-3" />
            <div>Chargement des machines...</div>
          </div>
        )}

        {!loading && machines.length === 0 && !error && (
          <div className="text-center py-16 text-white/40 text-sm">
            Aucun distributeur automatique actif.
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {machines.map((m) => (
            <MachineCard key={m.id} machine={m} />
          ))}
        </div>

        <div className="mt-16 pt-6 border-t border-white/5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          AgroManage · Vitrine machines · Données MongoDB (dernier sync Nayax auto 15 min)
        </div>
      </div>
    </div>
  );
}
